/**
 * Pure geometry helpers for the hosted-network display (PRD-04 FR1).
 *
 * - `decodePolyline` mirrors the backend `network_store/polyline.py` (Google encoded
 *   polyline, precision 5) — the network-routes overview ships geometry encoded.
 * - `simplifyLatLng` is a zoom-aware Douglas-Peucker so selectively-loaded full-res
 *   route shapes can be thinned client-side. Both are dependency-free + unit-tested.
 */

export type LatLng = [number, number];

/** Decode a Google encoded polyline (precision 5) to `[lat, lng]` pairs. */
export function decodePolyline(encoded: string, precision = 5): LatLng[] {
  const factor = Math.pow(10, precision);
  const coords: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  const len = encoded.length;
  while (index < len) {
    for (let isLng = 0; isLng < 2; isLng += 1) {
      let shift = 0;
      let result = 0;
      let b: number;
      do {
        b = encoded.charCodeAt(index) - 63;
        index += 1;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const delta = result & 1 ? ~(result >> 1) : result >> 1;
      if (isLng) lng += delta;
      else lat += delta;
    }
    coords.push([lat / factor, lng / factor]);
  }
  return coords;
}

/** Douglas-Peucker tolerance in degrees ≈ 1.5 web-mercator px at `zoom` (mirrors the backend). */
export function zoomToleranceDeg(zoom: number): number {
  const z = Math.max(0, Math.min(22, Math.floor(zoom)));
  const degPerPx = 360 / (256 * 2 ** z);
  return degPerPx * 1.5;
}

function sqSegDist(p: LatLng, a: LatLng, b: LatLng): number {
  let x = a[0];
  let y = a[1];
  let dx = b[0] - x;
  let dy = b[1] - y;
  if (dx !== 0 || dy !== 0) {
    const t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);
    if (t > 1) {
      x = b[0];
      y = b[1];
    } else if (t > 0) {
      x += dx * t;
      y += dy * t;
    }
  }
  dx = p[0] - x;
  dy = p[1] - y;
  return dx * dx + dy * dy;
}

/** Douglas-Peucker simplify of `[lat, lng]` coords; endpoints preserved (≥2 points). */
export function simplifyLatLng(coords: LatLng[], toleranceDeg: number): LatLng[] {
  if (toleranceDeg <= 0 || coords.length <= 2) return coords;
  const sqTol = toleranceDeg * toleranceDeg;
  const keep = new Array<boolean>(coords.length).fill(false);
  keep[0] = true;
  keep[coords.length - 1] = true;
  const stack: Array<[number, number]> = [[0, coords.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop() as [number, number];
    let maxSq = 0;
    let idx = -1;
    for (let i = first + 1; i < last; i += 1) {
      const sq = sqSegDist(coords[i], coords[first], coords[last]);
      if (sq > maxSq) {
        maxSq = sq;
        idx = i;
      }
    }
    if (maxSq > sqTol && idx !== -1) {
      keep[idx] = true;
      stack.push([first, idx], [idx, last]);
    }
  }
  return coords.filter((_, i) => keep[i]);
}

/** Convenience: simplify for a given map zoom. */
export function simplifyForZoom(coords: LatLng[], zoom: number): LatLng[] {
  return simplifyLatLng(coords, zoomToleranceDeg(zoom));
}
